import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Search } from 'lucide-react';
import { useUniversalSearch } from '../../../hooks/useUniversalSearch';
import DirectionPicker from '../../DirectionPicker';
import type { Direction } from '../../../lib/types';

const POPULAR = [
  { label: 'U.S. Bank Stadium', to: '/venues/us-bank-stadium' },
  { label: 'Target Field', to: '/venues/target-field' },
  { label: 'Lambeau Field', to: '/venues/lambeau-field' },
  { label: 'Wrigley Field', to: '/venues/wrigley-field' },
  { label: 'Michigan Stadium', to: '/venues/michigan-stadium' },
  { label: 'Ohio Stadium', to: '/venues/ohio-stadium' },
];

export default function SearchPanel() {
  const [query, setQuery] = useState('');
  const [direction, setDirection] = useState<Direction | null>(null);
  const { results, loading } = useUniversalSearch(query);
  const navigate = useNavigate();

  const withDirection = (href: string) => direction ? `${href}?dir=${direction}` : href;

  function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (results.length > 0) {
      navigate(withDirection(results[0].href));
    } else {
      navigate('/venues');
    }
  }

  return (
    <section className="bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-12">
        <div className="rounded-2xl border border-gray-200 bg-white p-6 sm:p-8 shadow-sm">
          <div className="grid lg:grid-cols-[1fr_1.3fr] gap-6 lg:gap-10 items-start">
            <h2 className="text-2xl sm:text-3xl font-bold tracking-tight text-gray-900 leading-snug">
              Where are you going, and which way are you driving home?
            </h2>

            <div>
              <p className="mb-2 text-xs font-bold uppercase tracking-wider text-gray-400">
                1. Choose your direction home
              </p>
              <DirectionPicker value={direction} onChange={setDirection} />

              <p className="mt-6 mb-2 text-xs font-bold uppercase tracking-wider text-gray-400">
                2. Find your venue or event
              </p>
              <form onSubmit={onSubmit} className="relative">
                <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                <input
                  type="text"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder="Search garages, venues, events, teams, or cities"
                  className="w-full pl-12 pr-4 py-3.5 rounded-xl border border-gray-200 bg-gray-50 text-gray-900 placeholder:text-gray-400 outline-none focus:border-gray-400 focus:bg-white transition-colors"
                />
              </form>

              {/* Live results dropdown */}
              {query.trim() && (
                <div className="mt-2 rounded-xl border border-gray-200 bg-white shadow-sm overflow-hidden">
                  {loading ? (
                    <div className="px-4 py-3 text-sm text-gray-400">Searching…</div>
                  ) : results.length === 0 ? (
                    <div className="px-4 py-3 text-sm text-gray-400">No matches yet.</div>
                  ) : (
                    results.slice(0, 6).map((r) => (
                      <Link
                        key={`${r.type}-${r.id}`}
                        to={withDirection(r.href)}
                        className="flex items-center justify-between px-4 py-2.5 hover:bg-gray-50 transition-colors"
                      >
                        <span className="text-sm font-medium text-gray-900">{r.name}</span>
                        <span className="text-xs text-gray-400 capitalize">{r.type}</span>
                      </Link>
                    ))
                  )}
                </div>
              )}

              <p className="mt-5 text-xs font-bold uppercase tracking-wider text-gray-400">
                Popular searches
              </p>
              <div className="mt-3 flex flex-wrap gap-2.5">
                {POPULAR.map((p) => (
                  <Link
                    key={p.label}
                    to={withDirection(p.to)}
                    className="inline-flex items-center gap-2 px-3 py-2 rounded-full border border-gray-200 bg-white hover:border-gray-300 hover:bg-gray-50 transition-colors"
                  >
                    <span
                      className="w-5 h-5 rounded-full shrink-0"
                      style={{ background: 'linear-gradient(135deg,#e2e8f0,#cbd5e1)' }}
                    />
                    <span className="text-xs font-medium text-gray-700 whitespace-nowrap">
                      {p.label}
                    </span>
                  </Link>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
